/**
 * The Settings form model (plan docs/plans/lms-plan.md 5.8, 9.1 to 9.3,
 * Appendix A "Settings and jobs"; 18.9, 18.26, 18.27; WP-48; R4, R23).
 *
 * The form holds text as typed; a save sends ONLY the fields that changed,
 * each validated here first with the same rules the route applies
 * (backend/src/routes/lms/settings.js FIELD_RULES), so a person sees what is
 * wrong beside the field before anything is sent. The server re-checks every
 * field and names the first bad one in a 400 (never echoing the value);
 * fieldFromServerMessage maps that message back to its field.
 *
 * Unchanged fields are not re-validated: a stored value the browser happens to
 * judge differently (a time zone its ICU lacks, say) never blocks another edit
 * (the route's rule for digest recipients is the same, 18.26).
 */
import type { LmsSettings, LmsSettingsPatch, SettingsField } from '../../../api/settings';
import { SETTINGS_FIELDS } from '../../../api/settings';

/** The bounds the route enforces (18.9, 18.26). */
export const LIMITS = {
  organizationMax: 200,
  signerMax: 120,
  reminderMin: 1,
  reminderMax: 90,
  overdueRepeat: [1, 365] as const,
  renewalLead: [0, 365] as const,
  dueDays: [1, 365] as const,
  passMark: [0, 100] as const,
  hour: [0, 23] as const,
  weekday: [1, 7] as const,
  digestRecipientsMax: 50,
};

/** The form as typed: numbers stay text until the save validates them. */
export interface SettingsFormState {
  organization_name: string;
  certificate_signer_name: string;
  certificate_signer_title: string;
  timezone: string;
  reminder_days: string;
  overdue_repeat_days: string;
  digest_hour_local: string;
  admin_digest_weekday: string;
  admin_digest_user_ids: string[];
  renewal_lead_days_default: string;
  default_due_days: string;
  default_pass_mark_pct: string;
}

export type SettingsErrors = Partial<Record<SettingsField, string>>;

/** The label each field carries on screen (and in its error message). */
export const FIELD_LABELS: Record<SettingsField, string> = {
  organization_name: 'Organization name',
  certificate_signer_name: 'Signer name',
  certificate_signer_title: 'Signer title',
  timezone: 'Time zone',
  reminder_days: 'Reminder days',
  overdue_repeat_days: 'Overdue repeat',
  digest_hour_local: 'Digest hour',
  admin_digest_weekday: 'Digest weekday',
  admin_digest_user_ids: 'Extra digest recipients',
  renewal_lead_days_default: 'Renewal lead',
  default_due_days: 'Default due window',
  default_pass_mark_pct: 'Default pass mark',
};

/** The time zones offered (WCR's sites are in the United States); a stored other one is kept and offered too. */
export const COMMON_TIME_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Puerto_Rico',
  'UTC',
];

/** ISO weekdays (9.3: 1 is Monday). */
export const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

/** "07:00" for 7. */
export function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

function text(value: string | null | undefined): string {
  return typeof value === 'string' ? value : '';
}

function num(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

/** The form a loaded settings row starts as. */
export function formFromSettings(s: LmsSettings): SettingsFormState {
  return {
    organization_name: text(s.organization_name),
    certificate_signer_name: text(s.certificate_signer_name),
    certificate_signer_title: text(s.certificate_signer_title),
    timezone: text(s.timezone),
    reminder_days: Array.isArray(s.reminder_days) ? s.reminder_days.join(', ') : '',
    overdue_repeat_days: num(s.overdue_repeat_days),
    digest_hour_local: num(s.digest_hour_local),
    admin_digest_weekday: num(s.admin_digest_weekday),
    admin_digest_user_ids: Array.isArray(s.admin_digest_user_ids) ? [...s.admin_digest_user_ids] : [],
    renewal_lead_days_default: num(s.renewal_lead_days_default),
    default_due_days: num(s.default_due_days),
    default_pass_mark_pct: num(s.default_pass_mark_pct),
  };
}

/** An IANA zone the browser knows (the route's isKnownTimeZone, the same Intl test). */
export function isKnownTimeZone(name: string): boolean {
  if (!name) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return true;
  } catch {
    return false;
  }
}

/** A whole number typed into a field (digits only), else null. */
export function parseWhole(raw: string): number | null {
  const t = raw.trim();
  if (!/^[0-9]+$/.test(t)) return null;
  const n = Number(t);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * "30, 14, 7, 1" -> [30, 14, 7, 1]: whole numbers 1 to 90, strictly
 * descending (so no repeats), at least one (18.27); null when any rule fails.
 * Commas, spaces or both separate the numbers.
 */
export function parseReminderDays(raw: string): number[] | null {
  const parts = raw.split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0) return null;
  const out: number[] = [];
  for (const part of parts) {
    const n = parseWhole(part);
    if (n === null || n < LIMITS.reminderMin || n > LIMITS.reminderMax) return null;
    if (out.length > 0 && !(n < out[out.length - 1])) return null;
    out.push(n);
  }
  return out;
}

/** The fields whose typed value differs from the loaded one. */
export function changedFields(form: SettingsFormState, initial: SettingsFormState): SettingsField[] {
  return SETTINGS_FIELDS.filter((field) => {
    const a = form[field];
    const b = initial[field];
    if (Array.isArray(a) && Array.isArray(b)) return a.length !== b.length || a.some((v, i) => v !== b[i]);
    return a !== b;
  });
}

type Checked = { ok: true; value: unknown } | { ok: false; error: string };

function wholeIn(raw: string, [min, max]: readonly [number, number], message: string): Checked {
  const n = parseWhole(raw);
  return n !== null && n >= min && n <= max ? { ok: true, value: n } : { ok: false, error: message };
}

function optionalText(raw: string, label: string): Checked {
  const t = raw.trim();
  if (t.length > LIMITS.signerMax) return { ok: false, error: `${label} must be at most ${LIMITS.signerMax} characters.` };
  // 18.26: empty becomes null (the certificate then prints the organization alone, Q6).
  return { ok: true, value: t === '' ? null : t };
}

/** One field's rule (the route's FIELD_RULES, with the messages a person reads). */
function checkField(field: SettingsField, form: SettingsFormState): Checked {
  switch (field) {
    case 'organization_name': {
      const t = form.organization_name.trim();
      if (!t) return { ok: false, error: 'Organization name is required: it is printed on every certificate.' };
      if (t.length > LIMITS.organizationMax) {
        return { ok: false, error: `Organization name must be at most ${LIMITS.organizationMax} characters.` };
      }
      return { ok: true, value: t };
    }
    case 'certificate_signer_name':
      return optionalText(form.certificate_signer_name, 'Signer name');
    case 'certificate_signer_title':
      return optionalText(form.certificate_signer_title, 'Signer title');
    case 'timezone':
      return isKnownTimeZone(form.timezone)
        ? { ok: true, value: form.timezone }
        : { ok: false, error: 'Time zone must be an IANA time zone name, for example America/New_York.' };
    case 'reminder_days': {
      if (!form.reminder_days.trim()) {
        return { ok: false, error: 'Reminder days need at least one threshold, a whole number from 1 to 90.' };
      }
      const days = parseReminderDays(form.reminder_days);
      return days
        ? { ok: true, value: days }
        : {
            ok: false,
            error: 'Reminder days must be whole numbers from 1 to 90, largest first, with no repeats (for example 30, 14, 7, 1).',
          };
    }
    case 'overdue_repeat_days':
      return wholeIn(form.overdue_repeat_days, LIMITS.overdueRepeat, 'Overdue repeat must be a whole number of days from 1 to 365.');
    case 'digest_hour_local':
      return wholeIn(form.digest_hour_local, LIMITS.hour, 'Digest hour must be a whole number from 0 to 23.');
    case 'admin_digest_weekday':
      return wholeIn(form.admin_digest_weekday, LIMITS.weekday, 'Digest weekday must be a whole number from 1 (Monday) to 7 (Sunday).');
    case 'admin_digest_user_ids': {
      const ids = form.admin_digest_user_ids;
      if (ids.length > LIMITS.digestRecipientsMax) {
        return { ok: false, error: `Extra digest recipients hold at most ${LIMITS.digestRecipientsMax} people.` };
      }
      return { ok: true, value: [...ids] };
    }
    case 'renewal_lead_days_default':
      return wholeIn(form.renewal_lead_days_default, LIMITS.renewalLead, 'Renewal lead must be a whole number of days from 0 to 365.');
    case 'default_due_days':
      return wholeIn(form.default_due_days, LIMITS.dueDays, 'Default due window must be a whole number of days from 1 to 365.');
    case 'default_pass_mark_pct':
      return wholeIn(form.default_pass_mark_pct, LIMITS.passMark, 'Default pass mark must be a whole number from 0 to 100.');
    default:
      return { ok: false, error: 'Unknown setting.' };
  }
}

/**
 * The PUT body for the changed fields, or the errors that stop it. The patch
 * is null when nothing changed or when any changed field is invalid.
 */
export function buildPatch(
  form: SettingsFormState,
  initial: SettingsFormState
): { patch: LmsSettingsPatch | null; errors: SettingsErrors; changed: SettingsField[] } {
  const changed = changedFields(form, initial);
  const errors: SettingsErrors = {};
  const patch: Record<string, unknown> = {};
  for (const field of changed) {
    const result = checkField(field, form);
    if (result.ok) patch[field] = result.value;
    else errors[field] = result.error;
  }
  const valid = Object.keys(errors).length === 0;
  return { patch: valid && changed.length > 0 ? (patch as LmsSettingsPatch) : null, errors, changed };
}

/**
 * The field a route 400 names: its messages start with the field key
 * ("reminder_days must be ...", "admin_digest_user_ids[2] is not ..."), or
 * null for a message about the body as a whole.
 */
export function fieldFromServerMessage(message: string): SettingsField | null {
  const m = /^([a-z_]+)(?:\[\d+\])?(?![a-z_])/.exec(message.trim());
  if (!m) return null;
  return (SETTINGS_FIELDS as readonly string[]).includes(m[1]) ? (m[1] as SettingsField) : null;
}
